import { debounce } from 'lodash';
import { auditLogs } from '../../../api';
import { isEnvProd } from '../../../config';
import {
  IAuditEnvType,
  IAuditItem,
  IAuditLogBody,
  IAuditModule,
  IAuditOperateType,
} from '../../../model/api-modules';
import { GameEnvType } from '../../../model/api-modules/env';
import { store } from '../../../store';

const ModuleItemMap: Record<IAuditModule, string[]> = {
  [IAuditModule.Organization]: [
    IAuditItem.ViewOrganizationMembers,
    IAuditItem.ViewOrganizationApplications,
    IAuditItem.ViewOrganizationGroups,
    IAuditItem.ViewOrganizationRoles,
    IAuditItem.ViewOrganizationGroup,
    IAuditItem.ViewOrganizationRole,
    IAuditItem.InviteOrganizationMember,
    IAuditItem.RemoveOrganizationMember,
    IAuditItem.DeleteOrganizationGroup,
    IAuditItem.EditOrganizationGroup,
    IAuditItem.AddNewOrganizationGroup,
    IAuditItem.AddNewOrganizationRole,
    IAuditItem.EditOrganizationRole,
    IAuditItem.DeleteOrganizationRole,
    IAuditItem.ApproveApplication,
    IAuditItem.RefuseApplication,
  ],

  [IAuditModule.Billing]: [IAuditItem.ViewBilling],

  [IAuditModule.Audithub]: [
    IAuditItem.ViewAudithubTask,
    IAuditItem.ViewAudithubOperationreport,
    IAuditItem.DownloadOperationreport,
  ],

  [IAuditModule.GameInfo]: [
    IAuditItem.ViewGameBasicInf,
    IAuditItem.CopyTitleKey,
    IAuditItem.EditGameTitle,
    IAuditItem.DeleteGameTitle,
  ],

  [IAuditModule.EnvironmentManagement]: [
    IAuditItem.ViewGameEnvironments,
    IAuditItem.AddGameEnvironment,
    IAuditItem.EditGameEnvironment,
    IAuditItem.DeleteGameEnvironment,
  ],

  [IAuditModule.EnvironmentSync]: [IAuditItem.GameEnvironmentSync],

  [IAuditModule.ServerControl]: [
    IAuditItem.ChooseOpenType,
    IAuditItem.CloseGameEnvironment,
  ],

  [IAuditModule.ClientAPIControl]: [
    IAuditItem.ViewClientAPIControl,
    IAuditItem.EnableClientAPI,
    IAuditItem.DisableClientAPI,
    IAuditItem.DiscardAllClientAPI,
    IAuditItem.PublishClientAPI,
  ],

  [IAuditModule.Setting]: [
    IAuditItem.ViewSetting,
    IAuditItem.EditStudio,
    IAuditItem.DeleteStudio,
    IAuditItem.SubmitStudioCertification,
    IAuditItem.EditStudioCertification,
  ],
};

const ServiceModuleNameMap: Record<string, string> = {
  AutoTask: 'Autotask',
  TextChat: 'Textchat',
  VoiceChat: 'Voicechat',
  GameItems: 'Gameitems',
  PrivacyAccess: 'Privacyaccess',
  HealthMonitor: 'Healthmonitor',
  RemoteConfig: 'Remoteconfig',
  LaunchUpdate: 'Launchupdate',
  DynamicUpdate: 'Dynamicupdate',
};

export interface IPostAuditLogParams {
  gameID?: string;
  envID?: number | string;
  envType?: GameEnvType;
  detail?: Record<string, any>; // json字符串, 传递操作详情, 比如接口的参数, 返回等
}

const debounceAuditLogs = debounce(
  (params: IAuditLogBody) => {
    console.log(`操作上报 [${params.item}]`, params);
    if (
      [
        IAuditModule.Organization,
        IAuditModule.Billing,
        IAuditModule.Audithub,
        IAuditModule.GameInfo,
        IAuditModule.Setting,
      ].includes(params.module as IAuditModule) ||
      params.item === IAuditItem.DeleteGameEnvironment ||
      params.item === IAuditItem.EditGameEnvironment
    ) {
      const studioID = store.getState().studio.selectStudio?.studioID;
      auditLogs({ ...params, studio_id: studioID });
      return;
    }
    auditLogs(params);
  },
  300,
  {
    leading: true,
    trailing: false,
  }
);

export const auditServiceReadLog = async (
  viewServiceValue = '',
  data?: IPostAuditLogParams
) => {
  const module = viewServiceValue.replace(/^View/, '');
  const formattedModule = ServiceModuleNameMap[module] || module;
  if (!formattedModule) {
    return;
  }
  const params = getAuditParams(
    viewServiceValue,
    IAuditOperateType.Read,
    data,
    formattedModule
  );
  if (!params) {
    return;
  }

  await debounceAuditLogs(params);
};

export const auditApplyServiceWriteLog = async (
  serviceName = '',
  data?: IPostAuditLogParams
) => {
  try {
    const module = serviceName.replace(/[\W_]/g, '');
    const formattedKey = `ApplyStart${ServiceModuleNameMap[module] || module}`;
    if (!formattedKey) {
      return;
    }
    const params = getAuditParams(
      formattedKey,
      IAuditOperateType.Write,
      data,
      serviceName
    );
    if (!params) {
      return;
    }

    await debounceAuditLogs(params);
  } catch (err) {}
};

export const auditReadLog = async (
  item: IAuditItem,
  data?: IPostAuditLogParams
) => {
  try {
    const params = getAuditParams(item, IAuditOperateType.Read, data);
    if (!params) {
      return;
    }

    await debounceAuditLogs(params);
  } catch (err) {}
};

export const auditWriteLog = async (
  item: IAuditItem,
  data: IPostAuditLogParams
) => {
  try {
    const params = getAuditParams(item, IAuditOperateType.Write, data);
    if (!params) {
      return;
    }

    await debounceAuditLogs(params);
  } catch (err) {}
};

const getAuditParams = (
  item: string,
  type: IAuditOperateType,
  data?: IPostAuditLogParams,
  auditModule?: string
) => {
  const { userInfo } = store.getState().common;
  const module =
    auditModule ||
    Object.keys(ModuleItemMap).find((moduleName) => {
      return ModuleItemMap[moduleName as IAuditModule].includes(item);
    });
  if (!module) {
    return null;
  }

  const detail =
    type === IAuditOperateType.Read
      ? {
          url: location.pathname,
          data: data?.detail || '',
          user: userInfo?.UserID || '',
        }
      : data?.detail;

  const getEnvType = () => {
    if (data?.envType === GameEnvType.Dev) {
      return IAuditEnvType.Dev;
    }
    if (data?.envType === GameEnvType.Live) {
      return IAuditEnvType.Prod;
    }
    return isEnvProd() ? IAuditEnvType.Prod : IAuditEnvType.Dev;
  };
  const params = {
    game_id: data?.gameID,
    env_id: data?.envID ? data.envID : undefined,
    env_type: getEnvType(),
    module,
    item,
    detail: detail || {},
    type,
  };

  return params;
};
